import stylesTmp from "./HelpList.module.scss"
import styles from "./Help.module.scss"
import {useEffect, useState} from "react";
import {useActions} from "../../hook/useActions";
import {useTypedSelector} from "../../hook/useTypedSelector";
import { useTranslation } from "react-i18next";
import ProgressBar from "../ProgressBar";

class ProblemsList {
    spiritual: boolean = false;
    psychological: boolean = false;
    health: boolean = false;
    spirits: boolean = false;
    relationships: boolean = false;
    financial: boolean = false;
    addiction: boolean = false;
    others: boolean = false;
    officials: boolean = false;
    law: boolean = false;
    deadInDreams: boolean = false;
    society: boolean = false;
}

interface HelpListItemParams {
    text: string;
    imgUrl: string;
    itemIndex: keyof ProblemsList;
    onSelected: (isTrue: boolean, itemKey: keyof ProblemsList) => void;
    selected?: boolean;
}

const HelpListItem: React.FC<HelpListItemParams> = ({text, imgUrl, itemIndex, onSelected, selected}) => {
    const [isSelected, setIsSelected] = useState<boolean | undefined>(selected);
    useEffect(() => {
        setIsSelected(selected);
    }, [selected]);
    const { t } = useTranslation();

    return (
        <div className={styles.itemContainer}>
            <div className={styles.itemSubContainer}>
                <div className={styles.itemTextContainer}>
                    <div className={styles.itemText}>{text}</div>
                </div>
                <img className={styles.itemImage} src={imgUrl} alt={"icon"}/>
                <div className={styles.optionsContainer}>
                    <div onClick={() => {
                        // compute new selected state and send it to parent so unchecking is handled correctly
                        const nextSelected = !(isSelected ?? false);
                        onSelected(nextSelected, itemIndex);
                        setIsSelected(nextSelected);
                    }}
                         className={styles.checkbox2 + ((isSelected !== undefined && isSelected) ? " " + styles.checked : "")}></div>
                    <div className={styles.yes}>{t("Buttons.Yes")}</div>
                </div>
            </div>
        </div>
    );
}

const HelpList = () => {
    const { t } = useTranslation();
    const [savedCity, setSavedCity] = useState<string>("");
    const [savedCountry, setSavedCountry] = useState<string>("");
    const [problems, setProblems] = useState<ProblemsList>(new ProblemsList());
    const [loadedProblems, setLoadedProblems] = useState<ProblemsList | undefined>(undefined);
    const [isHelpListChanged, setIsHelpListChanged] = useState<boolean>(false);
    const [isHelpListTouched, setIsHelpListTouched] = useState<boolean>(false);

    const {helpList} = useTypedSelector((state) => state.postPractice);

    const {GetHelpList, SendHelpList} = useActions();

    useEffect(() => {
        let country = localStorage.getItem("country") ?? "Ukraine";
        let city = localStorage.getItem("city") ?? "Rivne";
        setSavedCity(city);
        setSavedCountry(country);

        GetHelpList(city, country);
    }, []);

    useEffect(() => {
        if (helpList !== null && helpList.problemsListJSON !== "") {
            const lpl = JSON.parse(helpList.problemsListJSON) as ProblemsList;
            setLoadedProblems({...lpl});
            setProblems({...lpl});
        }
    }, [helpList]);

    const onItemSelected = (isTrue: boolean, itemKey: keyof ProblemsList) => {
        setProblems(prev => ({
            ...prev,
            [itemKey]: isTrue,
        }));
        setIsHelpListTouched(true);
        setIsHelpListChanged(true);
    }
    const onSubmit = () => {
        const data = {
            city: savedCity,
            country: savedCountry,
            problemsListJSON: JSON.stringify(problems),
        };
        SendHelpList(data);
        setIsHelpListChanged(false);
    }

    const totalProblems = Object.keys(problems).length || 1;
    const selectedCount = Object.keys(problems).reduce((acc, k) => {
        const val = problems[k as keyof ProblemsList];
        return acc + (val ? 1 : 0);
    }, 0);
    const progressPercent = Math.round((selectedCount / totalProblems) * 100);

    return (
        <div className={stylesTmp.frame20}>
            <HelpListItem onSelected={onItemSelected} selected={loadedProblems ? loadedProblems.spiritual : undefined}
                          itemIndex={"spiritual"}
                          text={t("HelpList.Spiritual")} imgUrl={"/images/icons/help-list/1_spirit.png"}/>
            <HelpListItem onSelected={onItemSelected}
                          selected={loadedProblems ? loadedProblems.psychological : undefined}
                          itemIndex={"psychological"}
                          text={t("HelpList.Psychological")} imgUrl={"/images/icons/help-list/2_psychology.png"}/>
            <HelpListItem onSelected={onItemSelected} selected={loadedProblems ? loadedProblems.health : undefined}
                          itemIndex={"health"}
                          text={t("HelpList.Health")} imgUrl={"/images/icons/help-list/3_health.png"}/>
            <HelpListItem onSelected={onItemSelected} selected={loadedProblems ? loadedProblems.spirits : undefined}
                          itemIndex={"spirits"}
                          text={t("HelpList.Spirits")} imgUrl={"/images/icons/help-list/4_demon.png"}/>
            <HelpListItem onSelected={onItemSelected}
                          selected={loadedProblems ? loadedProblems.relationships : undefined}
                          itemIndex={"relationships"}
                          text={t("HelpList.Relationships")} imgUrl={"/images/icons/help-list/5_releshionship.png"}/>
            <HelpListItem onSelected={onItemSelected} selected={loadedProblems ? loadedProblems.financial : undefined}
                          itemIndex={"financial"}
                          text={t("HelpList.Financial")} imgUrl={"/images/icons/help-list/6_finance.png"}/>
            <HelpListItem onSelected={onItemSelected} selected={loadedProblems ? loadedProblems.addiction : undefined}
                          itemIndex={"addiction"}
                          text={t("HelpList.Addiction")} imgUrl={"/images/icons/help-list/7_intoxication.png"}/>
            <HelpListItem onSelected={onItemSelected} selected={loadedProblems ? loadedProblems.others : undefined}
                          itemIndex={"others"}
                          text={t("HelpList.Others")} imgUrl={"/images/icons/help-list/8_from_other.png"}/>
            <HelpListItem onSelected={onItemSelected} selected={loadedProblems ? loadedProblems.officials : undefined}
                          itemIndex={"officials"}
                          text={t("HelpList.Officials")} imgUrl={"/images/icons/help-list/9_officials.png"}/>
            <HelpListItem onSelected={onItemSelected} selected={loadedProblems ? loadedProblems.law : undefined}
                          itemIndex={"law"}
                          text={t("HelpList.Law")} imgUrl={"/images/icons/help-list/10_law.png"}/>
            <HelpListItem onSelected={onItemSelected}
                          selected={loadedProblems ? loadedProblems.deadInDreams : undefined}
                          itemIndex={"deadInDreams"}
                          text={t("HelpList.DeadInDreams")} imgUrl={"/images/icons/help-list/11_died_in_a_dream.png"}/>
            <HelpListItem onSelected={onItemSelected} selected={loadedProblems ? loadedProblems.society : undefined}
                          itemIndex={"society"}
                          text={t("HelpList.Society")} imgUrl={"/images/icons/help-list/12_society.png"}/>


            <div className={stylesTmp.progressBarTitle}>{t("HelpList.ProblemsToSolve")}</div>
            <ProgressBar value={progressPercent}
                         barColor={"rgb(" + (progressPercent * 2) + ", " + (200 - (progressPercent * 2)) + ", 0)"}
                         hidePercentage={true}/>
            {
                (isHelpListChanged &&
                    <div className={stylesTmp.div15} onClick={onSubmit}>
                        <div className={stylesTmp.text9}>{t("Buttons.SubmitRequest")}</div>
                    </div>)
                ||
                (<div className={stylesTmp.div15 + " " + stylesTmp.disabled}>
                    {(isHelpListTouched &&
                            <div className={stylesTmp.text9}>{t("Buttons.Sent")}</div>)
                        ||
                        <div className={stylesTmp.text9}>{t("Buttons.SubmitRequest")}</div>}
                </div>)
            }
        </div>
    );
}

export default HelpList;
